import { useMemo, type CSSProperties } from 'react';
import { formatCountdown, formatClock } from '../utils/time.ts';

export type Urgency = 'normal' | 'warning' | 'critical';

/**
 * Role of a card — drives the visual styling.
 *   - rendu: live on air, red comet border
 *   - prise: next live, counting down
 *   - info:  the live AFTER the next one (N+2) — a start time, never a
 *            countdown, visually subordinate to the PRISE beside it
 */
export type CardKind = 'rendu' | 'prise' | 'info';

/** Derive urgency level from remaining seconds. */
export function getUrgency(seconds: number | undefined): Urgency {
  if (seconds === undefined) return 'normal';
  if (seconds <= 10) return 'critical';
  if (seconds <= 30) return 'warning';
  return 'normal';
}

interface Props {
  /** Card header label (e.g. "Fin du direct dans", "Prochain direct dans"). */
  label: string;
  /** Drives the red-comet border (rendu) vs. neutral border (prise). */
  kind?: CardKind;
  /** Clip / live name. */
  clipName?: string;
  /** Countdown in whole seconds (already snapped to NTP boundary). */
  seconds?: number;
  /** Scheduled time for the next live (displayed as "À HH:mm:ss"). */
  scheduledAt?: Date;
  /** IANA timezone used to render `scheduledAt` (per-channel). */
  timeZone: string;
  /** Visual urgency level. */
  urgency?: Urgency;
  /** True = empty placeholder with the given `emptyLabel`. */
  empty?: boolean;
  /** Text shown in the centre of an empty card (e.g. "AUCUN DIRECT EN COURS"). */
  emptyLabel?: string;
}

const cardLayoutBase = '@container rounded-xl flex flex-col';
const cardSize = 'px-8 py-8 md:px-10 md:py-10 min-h-[260px] md:min-h-[320px]';
const pillText = 'text-xs md:text-sm';
const pillIconSize = 'h-3 w-3 md:h-3.5 md:w-3.5';
// `countdown-digits`: fallback size for browsers without `cqw` (Safari ≤ 15),
// defined in index.css. Inert everywhere else.
const countdownText = 'countdown-digits text-[clamp(3.75rem,20.5cqw,9.5rem)]';
/**
 * Start time on an info card. Deliberately about half the countdown's size,
 * and grey: it sits next to a live countdown and must never be read as one.
 * Sized with breakpoints, NOT container units — `cqw` is what breaks the
 * countdown on Safari ≤ 15 (iPadOS 15.8). Checked to fit "À 18:00:00"
 * (6 em of monospace) in the card's inner width at every breakpoint.
 */
const infoTimeText = 'text-4xl lg:text-5xl xl:text-6xl';
const clipNameText = 'text-2xl md:text-3xl';
const scheduledText = 'text-xs md:text-sm';
const emptyLabelText = 'text-xs';

const neutralLabel = 'text-white';
const neutralName = 'text-neutral-200';
const neutralCaption = 'text-white';

const urgencyCountdown: Record<Urgency, string> = {
  normal: 'text-neutral-100',
  warning: 'text-orange-400 transition-colors duration-300',
  critical:
    'text-red-500 [text-shadow:0_0_30px_rgba(239,68,68,0.5)] animate-card-pulse transition-colors duration-300',
};

export function CountdownCard({
  label,
  kind,
  clipName,
  seconds,
  scheduledAt,
  timeZone,
  urgency = 'normal',
  empty = false,
  emptyLabel = 'Aucun direct en cours',
}: Props) {
  // Wall-clock-synced delay so remounts pick up the current rotation phase
  // instead of restarting at 0°. Computed once on mount; keyframe cycle is 6s.
  //
  // MUST stay above every early return — it is this component's only hook.
  // Below `if (empty) return` it only worked because a card never switches
  // between empty and non-empty under the same React key; a hook order that
  // depends on a render branch is a crash waiting for the next variant.
  const renduStyle = useMemo<CSSProperties>(
    () => ({ '--rendu-delay': `${-(Date.now() % 6000)}ms` } as CSSProperties),
    [],
  );

  if (empty) {
    return (
      <div
        className={`${cardLayoutBase} ${cardSize} items-center justify-center border border-dashed border-neutral-800`}
      >
        {/* text-center matters once a label wraps — on a narrow card the two
            lines would otherwise be left-aligned inside a centred block. */}
        <div className={`${emptyLabelText} text-center uppercase tracking-[0.25em] text-neutral-600`}>
          {emptyLabel}
        </div>
        <div className="mt-3 text-neutral-700">—</div>
      </div>
    );
  }

  const isRendu = kind === 'rendu';
  const isInfo = kind === 'info';
  const cardClass = `${cardLayoutBase} ${cardSize} justify-between bg-white/[0.025] ${
    isRendu
      ? 'border border-red-500/40 rendu-border-glow'
      : 'border border-neutral-800'
  }`;

  return (
    <div className={cardClass} style={isRendu ? renduStyle : undefined}>
      <div className="flex items-center justify-between gap-4">
        <span
          className={`inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-3 py-1 font-semibold uppercase tracking-[0.15em] ${pillText} ${neutralLabel}`}
        >
          {kind === 'info' ? (
            <InfoIcon className={pillIconSize} />
          ) : (
            kind && <CardIcon kind={kind} className={pillIconSize} />
          )}
          {label}
        </span>
        {/* On an info card the time IS the main line — no duplicate here. */}
        {scheduledAt && !isInfo && (
          <span className={`font-semibold uppercase tracking-[0.15em] ${scheduledText} ${neutralCaption}`}>
            A {formatClock(scheduledAt, timeZone)}
          </span>
        )}
      </div>
      {/* RULE: an info card NEVER renders a countdown, whatever `seconds`
          holds — a stray `{...spec, seconds: 0}` from the carousel's freeze
          stays harmless. The "À" prefix is what stops a time of day being
          read as a countdown at a glance. */}
      <div
        className={`font-mono tabular-nums font-bold leading-none ${
          isInfo ? `text-neutral-400 ${infoTimeText}` : `${urgencyCountdown[urgency]} ${countdownText}`
        }`}
      >
        {isInfo
          ? `À ${scheduledAt ? formatClock(scheduledAt, timeZone) : '--:--:--'}`
          : seconds !== undefined
            ? formatCountdown(seconds)
            : '--:--:--'}
      </div>
      <div className={`font-semibold ${clipNameText} ${neutralName}`}>
        {clipName ? clipName.replace(/_/g, ' ') : '\u00A0'}
      </div>
    </div>
  );
}

/**
 * Tiny inline SVG icon shown inside the label pill.
 *   - prise = green play triangle (entering the air)
 *   - rendu = blue stop square (leaving the air)
 *
 * `info` is excluded from the type on purpose: this function's fallback is
 * the RENDU square, so an unhandled info card would show "leaving the air" on
 * an "entering the air" card — and TypeScript would not say a word.
 */
function CardIcon({ kind, className }: { kind: Exclude<CardKind, 'info'>; className: string }) {
  if (kind === 'prise') {
    return (
      <svg viewBox="0 0 12 12" aria-hidden="true" className={`fill-green-500 ${className}`}>
        <path d="M3 1.5 L10 6 L3 10.5 Z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 12 12" aria-hidden="true" className={`fill-blue-500 ${className}`}>
      <rect x="2" y="2" width="8" height="8" rx="1" />
    </svg>
  );
}

/**
 * Same play triangle as PRISE — it is an entry into the air too — but grey:
 * it belongs to the PRISE family without its imminence.
 */
function InfoIcon({ className }: { className: string }) {
  return (
    <svg viewBox="0 0 12 12" aria-hidden="true" className={`fill-neutral-500 ${className}`}>
      <path d="M3 1.5 L10 6 L3 10.5 Z" />
    </svg>
  );
}
